import { useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router";
import _ from "lodash";

import { AssistantOutlinedIcon, EditIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import IconButton from "components/IconButton";
import NoPrint from "components/NoPrint";
import SanitizeHtml from "components/SanitizeHtml";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useAIMemoSection } from "components/useAIMemoSection";

import AIAnswerSectionEditDialog from "entities/Memo/components/Sections/AIAnswerSection/AIAnswerSectionEditDialog";
import MemoSectionContextButton from "entities/Memo/components/Sections/ContextButton";
import SectionHeader from "entities/Memo/components/Sections/SectionHeader";
import { IMemoSection } from "entities/Memo/sdk";
import { useAITableDisplayMode } from "entities/Memo/utils";

export interface ISectionAIAnswer {
  section: IMemoSection;
  onUpdate: () => void;
  externalToken?: string;
}
const SectionAIAnswer = ({
  section,
  onUpdate,
  externalToken,
}: ISectionAIAnswer) => {
  const params = useParams<{
    memoId: string;
    dealId: string;
  }>();
  const [searchParams] = useSearchParams();

  const dealId = useMemo(() => Number(params.dealId), [params.dealId]);
  const memoSectionId = useMemo(
    () =>
      searchParams.get("memoSectionId")
        ? Number(searchParams.get("memoSectionId"))
        : null,
    [searchParams]
  );

  const {
    selectedAIMemoSectionIdForEditDialog,
    handleEditDialogAIMemoSectionIdChange,
  } = useAIMemoSection();

  const { sanitizeHtmlOptions, sanitizeHtmlContainerStyles } =
    useAITableDisplayMode({
      dealId,
      externalToken,
    });

  const handleEditButtonClick = useCallback(
    () =>
      handleEditDialogAIMemoSectionIdChange({
        sectionId: section.section_id,
      }),
    [handleEditDialogAIMemoSectionIdChange, section.section_id]
  );

  const field = useMemo(
    () => (!_.isNil(_.first(section.fields)) ? _.first(section.fields) : null),
    [section]
  );

  const lastSavedAt = useMemo(
    () => _.first(field?.values)?.updated_at || null,
    [field]
  );

  const fieldValue = useMemo(() => {
    if (section.is_prompt_response_in_progress) {
      return null;
    }
    if (
      !_.isNil(field?.values) &&
      !_.isEmpty(field?.values) &&
      !section.is_prompt_response_in_progress
    ) {
      return _.get(_.first(field.values), "rich_value", null);
    }
    return null;
  }, [field?.values, section.is_prompt_response_in_progress]);

  const placeholder = useMemo(
    () =>
      section.is_prompt_response_in_progress
        ? "An answer is currently being generated..."
        : "AI-generated answer | No content yet...",
    [section.is_prompt_response_in_progress]
  );

  const onClose = useCallback(
    () => handleEditDialogAIMemoSectionIdChange?.({ sectionId: null }),
    [handleEditDialogAIMemoSectionIdChange]
  );

  const onUpdateWithSuccessMessage = useCallback(
    ({ message }: { message?: string }) => {
      onUpdate();
      if (!_.isNil(message)) {
        toast.successMessage(message);
      }
    },
    [onUpdate]
  );

  return _.isNil(field) ? null : (
    <Stack gap={2}>
      <SectionHeader
        label={
          <Stack direction="row" alignItems="center" gap={0.8}>
            <Text
              variant="text1"
              sx={{ fontWeight: "bold", display: "inline" }}
            >
              {section.title}
            </Text>
            <NoPrint sx={{ display: "flex", alignItems: "center" }}>
              <AssistantOutlinedIcon sx={{ width: "20px", height: "20px" }} />
            </NoPrint>
          </Stack>
        }
        isFocused={section.section_id === memoSectionId}
        alignActionButton={section ? "right" : "left"}
        actionButton={
          _.isUndefined(externalToken) && (
            <NoPrint>
              <Stack direction="row" spacing={1} alignItems="center">
                {!section.is_na && !_.isNil(section.ai_prompt) && (
                  <>
                    <Tooltip
                      title={
                        section.is_prompt_response_in_progress
                          ? "Please wait until the answer generation finishes."
                          : undefined
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleEditButtonClick}
                          disabled={section.is_prompt_response_in_progress}
                        >
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <AIAnswerSectionEditDialog
                      name={section.title}
                      memoSectionId={section.section_id}
                      isOpen={
                        selectedAIMemoSectionIdForEditDialog ===
                        section.section_id
                      }
                      onUpdate={onUpdateWithSuccessMessage}
                      onClose={onClose}
                      fieldId={field.company_custom_field_id}
                      fieldValue={fieldValue}
                      aiPromptSection={section.ai_prompt}
                      isPromptResponseInProgress={
                        section.is_prompt_response_in_progress
                      }
                      lastSavedAt={lastSavedAt}
                    />
                  </>
                )}
                {section && (
                  <MemoSectionContextButton
                    dealId={dealId}
                    section={section}
                    onUpdate={onUpdate}
                    isPromptInProgress={section.is_prompt_response_in_progress}
                  />
                )}
              </Stack>
            </NoPrint>
          )
        }
      />
      <Stack padding={theme.spacing(0, 2)} sx={sanitizeHtmlContainerStyles}>
        {section.is_na ? (
          <Text sx={{ opacity: 0.5 }}>N/A</Text>
        ) : !_.isNil(fieldValue) ? (
          <SanitizeHtml html={fieldValue} options={sanitizeHtmlOptions} />
        ) : (
          <Text sx={{ color: colors.gray60 }}>{placeholder}</Text>
        )}
      </Stack>
    </Stack>
  );
};

export default SectionAIAnswer;
